import Config from 'config/settings';
import HomeColours from 'app/views/home/config/HomeColours';
import HomeFieldConfig from 'app/views/home/webgl/particles/config/HomeFieldConfig';
import { device } from 'utils/device';

import {
	BEHAVIOUR_BREATHING,
	BEHAVIOUR_DISABLE_TIMELINE_X,
	BEHAVIOUR_PATH_ORBIT,
	BEHAVIOUR_PROGRESS_PARALLAX,
	BEHAVIOUR_REPELL,
	FLAG_ACTIVE,
	PATH_FLAG_SCALE_ACTIVE,
	PATH_FLAG_SYNC_ROTATION,
	REPELL_PUSH
} from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import { FRAME_SOLID2, FRAME_TRI, FRAME_TRI2 } from 'app/views/home/webgl/particlesOptimised/core/frames';
import { LOOKUP_CIRCLE } from 'app/views/home/webgl/particlesOptimised/core/pathLookups';
import { hexToTint, particleRandom } from 'app/views/home/webgl/particlesOptimised/departments/departments.types';

import type { Vector2 } from 'app/contracts/Geometry';
import type { ParticleCore, PoolRange } from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import type { DepartmentDeps } from 'app/views/home/webgl/particlesOptimised/departments/departments.types';
import type { DepartmentScene } from 'app/views/home/webgl/webgl.types';

/**
 * IconDepartment (particles-optimised) — FORK of
 * webgl/particles/departments/IconDepartment.ts + IconParticleProcessor,
 * re-expressed as lane writes over the icon pool. The debug.io logo shape
 * comes from the SAME pixel grid the reference uses: LogoPixelCache.data
 * (handed in by the facade), laid out through a verbatim port of
 * ImageGridLayout.get + ParticleField.reposition's per-particle loop.
 *
 * Structure mapping (reference -> here):
 *  - ctor pool activate + _setIntialCentered -> initialState() (called by
 *    the facade on its first zone-ready tick, because the hard position
 *    snap needs the live containerX the reference ctor reads off
 *    sceneProps — the reference ctor runs ~1s into the field init, ours
 *    would run before the first frame);
 *  - startAsActiveScene/_toIconParticles/_startIconScaleUp/
 *    _increaseIconSizePass -> same names, same constants (229x352 grid at
 *    1.3 start scale, +6px/pass decaying by 0.98 over 450 passes at 1
 *    frame/pass, zone.scale/zone.cy placement);
 *  - IconParticleProcessor.initIcon/_triggerHomeEffectSwitch ->
 *    _initIcon/_triggerHomeEffectSwitch (per-lane);
 *  - tick's spawn block -> ported including the reference's own kill switch
 *    (_spawnCounter = 1e13 right after the random assignment): the switch
 *    fires ONCE on the first active tick, re-randomising every particle's
 *    ease/orbit/breathing;
 *  - switchHomeEffect's counter writes are NOT ported: the reference never
 *    calls counter.start() on the icon counter, so its count stays NaN and
 *    the counter can never fire (dead, bug-compatible by omission);
 *  - the 20ms DelayHelper before the xMul/yMul randomise -> a 1.2-frame
 *    ScriptList entry (same tick-time domain DelayHelper runs in);
 *  - startSceneDrag/endSceneDrag: reference bodies are disabled by early
 *    returns — no-ops here;
 *  - setIconPixelData's `HomeFieldConfig.iconParticles = data.length`
 *    write is NOT ported: the reference mutation lands after the partition
 *    cache (indexes()) is built, so it never affects pool ranges — a dead
 *    write on the home flow, skipped rather than replicated against the
 *    shared config object.
 *
 * Repell centre note: initIcon targets homeMouse = raw mouse cords minus
 * particleContainersX.home — the facade passes exactly that per tick
 * (mouse.x - context.homeContainerX) as this pool's repell centre.
 */
//?iconCount high-count reveal (iconDensity.ts): above this active count, the
//one-time per-particle reveal setup is drained in chunks across frames instead
//of all in one frame — so the reveal's O(n) init (initIcon rolls + the per-lane
//scripts.addMs closures) can't spike. At/below it (the shipped ~1571 logo) the
//reveal is byte-identical.
const ICON_REVEAL_CHUNK_THRESHOLD = 5000;
const ICON_REVEAL_CHUNK = 1500;

export default class IconDepartment implements DepartmentScene {
	private _deps: DepartmentDeps;
	private _core: ParticleCore;
	private _range: PoolRange;

	//Mobile pool cap (reference: pool sliced to iconParticlesMobile):
	private _activeCount: number;
	private _mobileMode: boolean = false;

	private _iconPixelData: Array<Vector2>;

	private _spawnCounter: number = 0;
	private _active: boolean = false;

	private _iconScaleUpFrameCounter = 0;
	private _iconScaleUpFrames = 3;
	private _iconScaleUpActive = false;
	private _iconGridWidth: number;
	private _iconGridHeight: number;
	private _iconAddEachPass = 4;
	private _iconReduceEachPass = 0.99;
	private _iconScaleUpPassCount = 0;
	private _iconScaleUpTotalPasses = 350;
	private _targetZone = null;
	private _fromIntro: boolean = true;
	private _delayedScaleUp: ReturnType<typeof setTimeout> = null;

	//High-count reveal chunking (see ICON_REVEAL_CHUNK_THRESHOLD). Off for the
	//stock logo, so its reveal path is untouched.
	private _chunkedReveal: boolean = false;
	private _revealActive: boolean = false;
	private _revealFrame: number = 0;
	private _revealTotalFrames: number = 0;

	constructor(deps: DepartmentDeps) {
		this._deps = deps;
		this._core = deps.core;
		this._range = deps.core.pool('icon');

		this._mobileMode = device.innerWidth <= Config.mobileBreakpoint;
		this._activeCount = this._mobileMode
			? Math.min(HomeFieldConfig.iconParticlesMobile, this._range.count)
			: this._range.count;

		//Only a greatly-densified logo (the ?iconCount dev knob) chunks its reveal;
		//the stock count stays on the original one-frame path.
		this._chunkedReveal = this._activeCount > ICON_REVEAL_CHUNK_THRESHOLD;
	}

	/** The logo pixel grid (LogoPixelCache.data — the facade's pixel-data
	 *  handshake source, reused read-only). */
	public setIconPixelData(iconPixelData: Array<Vector2>): void {
		this._iconPixelData = iconPixelData;
	}

	/**
	 * The reference constructor's pool state (activate() + _setIntialCentered):
	 * every icon particle parked hard at the centre point, targetAlpha 1,
	 * targetScale 0 (invisible until the logo forms), default-state eases
	 * (x/y/scale 1, alpha 3 — ParticleProperties ctor + _defaultState).
	 * Facade calls this once, on its first zone-ready tick.
	 */
	public initialState(): void {
		const core = this._core;

		for (let n = 0; n < this._activeCount; n++) {
			const lane = this._range.start + n;

			core.behaviourMask[lane] = BEHAVIOUR_PROGRESS_PARALLAX;
			core.stateFlags[lane] |= FLAG_ACTIVE;
			core.textureId[lane] = FRAME_SOLID2;

			core.easeX[lane] = 1;
			core.easeY[lane] = 1;
			core.easeScale[lane] = 1;
			core.easeAlpha[lane] = 3;

			this._setCenteredLane(lane);

			core.tAlpha[lane] = 1;
			core.tScale[lane] = 0;
			core.scale[lane] = 0;
		}
	}

	//DepartmentScene surface
	//-----------------------------------------------------------------------------------------------

	public tick(): void {
		if (!this._active) {
			return;
		}

		//_positionParticleContainer (shared value computed by the facade):
		this._deps.stage.iconContainer.x = this._deps.context.homeContainerX;

		this._iconScaleUpTick();

		//High-count reveal (chunkedReveal): drain a slice of the deferred per-lane
		//init each frame so the reveal fills in over ~a dozen frames instead of
		//spiking one. The switchHomeEffect loop below is parked (spawnCounter set
		//to 1e13 in _reposition) because the drain does that per-lane work itself.
		if (this._revealActive) {
			this._drainReveal();
		}

		//The home-effect switch — reference block verbatim, including its own
		//kill switch: the 180+rand*30 respawn cadence is immediately
		//overwritten with 1e13, so the switch fires exactly once (first
		//active tick) and then never again. flags.transitioning gates it in
		//the reference; this type has no work transitions yet (always false):
		this._spawnCounter--;
		if (this._spawnCounter <= 0) {
			this._spawnCounter = Math.round(180 + Math.random() * 30);
			this._spawnCounter = 10000000000000;

			for (let n = 0; n < this._activeCount; n++) {
				this._switchHomeEffect(this._range.start + n);
			}
		}
	}

	public startAsActiveScene(): void {
		this._active = true;
		this._deps.stage.iconContainer.visible = true;
		this._toIconParticles();
	}

	public stopAsActiveScene(): void {
		this._active = false;
		this._deps.stage.iconContainer.visible = false;

		//deactivateAllBehaviours() [baseActive false] + active = false:
		const core = this._core;
		for (let n = 0; n < this._activeCount; n++) {
			const lane = this._range.start + n;
			core.behaviourMask[lane] = 0;
			core.stateFlags[lane] &= ~FLAG_ACTIVE;
		}
	}

	/** Reference bodies are disabled by early returns — kept as no-ops. */
	public startSceneDrag(): void {}

	public endSceneDrag(): void {}

	public resize(): void {}

	/** NEVER routed in the reference: HomeField.setupForWork/showFromWork
	 *  only route screens/spaces/systems ('icon' falls through the else
	 *  branches to spaces/systems respectively). Kept as contract no-ops. */
	public setupForWork(): void {}

	public showFromWork(): void {}

	public fromWorkToHome(): void {}

	/** Facade destroy() hook (no reference equivalent): clear the delayed
	 *  scale-up so it cannot fire into a torn-down scene. */
	public destroy(): void {
		if (this._delayedScaleUp) {
			clearTimeout(this._delayedScaleUp);
			this._delayedScaleUp = null;
		}
	}

	//Private — layout
	//-----------------------------------------------------------------------------------------------

	/** _setIntialCentered's per-particle position.set(pos, true): hard reset
	 *  in TIMELINE mode (disableTimelineX is not set yet at this point) —
	 *  target = absX + containerX (+ drag offset, zero at power 0). */
	private _setCenteredLane(lane: number): void {
		const core = this._core;
		const sizes = this._deps.getSizes();
		const isMobile = device.innerWidth < 1200;

		core.relX[lane] = isMobile ? 0.01 : -0.042;
		core.relY[lane] = 0.5;
		core.xOffset[lane] = 0;
		core.parallaxPower[lane] = 0;

		core.tx[lane] = core.pTx[lane] = core.relX[lane] * sizes.virtualWidth + this._deps.context.containerX;
		core.ty[lane] = core.pTy[lane] = core.relY[lane] * sizes.innerHeight;
		core.x[lane] = core.tx[lane];
		core.y[lane] = core.ty[lane];
	}

	private _setIntialCentered(): void {
		const core = this._core;

		for (let n = 0; n < this._activeCount; n++) {
			const lane = this._range.start + n;
			this._setCenteredLane(lane);
			core.tAlpha[lane] = 1;
			core.tScale[lane] = 0;
		}
	}

	/** _toIconParticles: compute the logo grid box off the home zone, park
	 *  everything at the centre, then ease out to the pixel-grid spots. */
	private _toIconParticles(): void {
		const sizes = this._deps.getSizes();

		let startScale = 1.3;
		if (device.innerWidth < 600) {
			startScale *= 0.6;
		}

		const zone = sizes.zones.home;
		const nw = 229 * startScale;
		const nh = 352 * startScale;

		const sizeW = (nw / sizes.virtualWidth) * zone.scale;
		const sizeH = (nh / device.innerHeight) * zone.scale;

		const posX = 0 - sizeW / 2 + 0.0005;
		const posY = zone.cy - sizeH / 2;

		if (this._fromIntro) {
			this._setIntialCentered();
		}

		this._reposition(posX, posY, sizeW, sizeH, true);

		this._iconGridWidth = nw;
		this._iconGridHeight = nh;
		this._targetZone = zone;

		this._iconScaleUpActive = false;

		if (this._fromIntro) {
			this._startIconScaleUp();
		} else {
			//Reference uses a native setTimeout here:
			if (this._delayedScaleUp) {
				clearTimeout(this._delayedScaleUp);
			}
			this._delayedScaleUp = setTimeout(() => {
				this._delayedScaleUp = null;
				this._startIconScaleUp();
			}, 400);
		}

		this._fromIntro = false;
	}

	/**
	 * ParticleField.reposition(IMAGE_PIXEL_DATA, ...) + ImageGridLayout.get,
	 * ported: rel targets = pos + pixel * size (NOT hard — the eases carry
	 * the particles in), xOffset zeroed. skipItems (mobile) spreads the full
	 * pixel table across the smaller pool (step = len / count, index =
	 * round(step * id) — max index stays in range, so the reference's
	 * missing-point fallback branch is unreachable and not ported). The
	 * pixel points carry no scale/alpha, so those writes don't apply; the
	 * targetRotation write lands in the reference's dead rotation Inertia
	 * (never reaches the sprite) and is skipped.
	 */
	private _reposition(posX: number, posY: number, sizeW: number, sizeH: number, firstPass: boolean): void {
		const core = this._core;
		const data = this._iconPixelData;
		const count = Math.min(this._activeCount, data.length);
		const skip = this._mobileMode;
		const step = skip && count < data.length ? data.length / count : 1;

		for (let id = 0; id < count; id++) {
			const i = skip ? Math.round(step * id) : id;
			const lane = this._range.start + id;

			core.xOffset[lane] = 0;
			core.relX[lane] = posX + data[i].x * sizeW;
			core.relY[lane] = posY + data[i].y * sizeH;

			if (firstPass) {
				//High-count path defers the heavy per-lane init to _drainReveal
				//(positions above are still set now, so the logo shape is fixed;
				//lanes stay scale-0/invisible until their chunk activates them).
				if (!this._chunkedReveal) {
					this._afterParticlePostion(lane);
				}
			} else {
				this._increaseOrbitExpand(lane);
			}
		}

		if (firstPass && this._chunkedReveal) {
			this._revealActive = true;
			this._revealFrame = 0;
			this._revealTotalFrames = Math.max(1, Math.ceil(this._activeCount / ICON_REVEAL_CHUNK));
			//Park the one-shot switchHomeEffect tick loop — the drain runs that
			//per-lane pass itself, chunked:
			this._spawnCounter = 10000000000000;
		}
	}

	/** High-count reveal drain: spread the one-time per-lane init over
	 *  _revealTotalFrames frames (the full setup both original reveal passes do —
	 *  `_afterParticlePostion` + `_switchHomeEffect`), so the O(n) cost can't spike
	 *  a single frame.
	 *
	 *  To keep the ORIGINAL "burst from centre" look (not a directional wipe): each
	 *  lane is re-snapped to the logo centre right before it activates (its rel
	 *  target is already the logo spot, so it eases out = a burst), and the lanes
	 *  are visited INTERLEAVED (n = frame + i*total) so every frame activates a set
	 *  scattered across the whole logo — the shape materialises uniformly out of the
	 *  centre instead of sweeping in from one side. */
	private _drainReveal(): void {
		const core = this._core;
		const sizes = this._deps.getSizes();
		const isMobile = device.innerWidth < 1200;
		//The _setCenteredLane centre, computed once per frame (not per lane):
		const cx = (isMobile ? 0.01 : -0.042) * sizes.virtualWidth + this._deps.context.containerX;
		const cy = 0.5 * sizes.innerHeight;

		const total = this._revealTotalFrames;
		const frame = this._revealFrame;
		const start = this._range.start;

		for (let i = 0; i < ICON_REVEAL_CHUNK; i++) {
			const n = frame + i * total;
			if (n >= this._activeCount) {
				break;
			}
			const lane = start + n;
			//Re-centre the live position only (rel target stays the logo spot) so
			//the particle bursts out from centre when it becomes visible here:
			core.x[lane] = core.tx[lane] = core.pTx[lane] = cx;
			core.y[lane] = core.ty[lane] = core.pTy[lane] = cy;
			this._afterParticlePostion(lane);
			this._switchHomeEffect(lane);
		}

		this._revealFrame++;
		if (this._revealFrame >= total) {
			this._revealActive = false;
		}
	}

	/** reposition onStart (first pass): activate + initIcon + the 7% teal. */
	private _afterParticlePostion(lane: number): void {
		this._core.stateFlags[lane] |= FLAG_ACTIVE;
		this._initIcon(lane);

		if (Math.random() < 0.07) {
			this._core.tint[lane] = hexToTint('#069c8a');
		}
	}

	//Private — scale-up passes
	//-----------------------------------------------------------------------------------------------

	private _startIconScaleUp(): void {
		this._iconScaleUpActive = true;
		this._iconScaleUpFrameCounter = this._iconScaleUpPassCount = 0;
		this._iconAddEachPass = 6;
		this._iconScaleUpTotalPasses = 450;
		this._iconReduceEachPass = 0.98;
		this._iconScaleUpFrames = 1;
	}

	private _iconScaleUpTick(): void {
		if (!this._iconScaleUpActive) {
			return;
		}

		this._iconScaleUpFrameCounter += this._deps.context.deltaTime;
		if (this._iconScaleUpFrameCounter >= this._iconScaleUpFrames) {
			this._iconScaleUpFrameCounter = 0;
			this._increaseIconSizePass();
		}
	}

	private _increaseIconSizePass(): void {
		const sizes = this._deps.getSizes();

		this._iconGridWidth += this._iconAddEachPass;
		this._iconGridHeight += this._iconAddEachPass;
		this._iconAddEachPass *= this._iconReduceEachPass;

		const sizeW = (this._iconGridWidth / sizes.virtualWidth) * this._targetZone.scale;
		const sizeH = (this._iconGridHeight / device.innerHeight) * this._targetZone.scale;

		const posX = 0 - sizeW / 2 + 0.0005;
		const posY = this._targetZone.cy - sizeH / 2;

		this._reposition(posX, posY, sizeW, sizeH, false);

		this._iconScaleUpPassCount++;
		if (this._iconScaleUpPassCount >= this._iconScaleUpTotalPasses) {
			this._iconScaleUpActive = false;
		}
	}

	/** IconParticleProcessor.increaseOrbitExpand: target-based ratchet. */
	private _increaseOrbitExpand(lane: number): void {
		if (this._core.orbitPath.expandTarget[lane] < 27) {
			this._core.orbitPath.expandTarget[lane] += 0.1;
		}
	}

	//Private — per-lane state (IconParticleProcessor)
	//-----------------------------------------------------------------------------------------------

	/** IconParticleProcessor.initIcon(reset = true), lane-for-line. */
	private _initIcon(lane: number): void {
		const core = this._core;
		const orbit = core.orbitPath;

		//repell.target = homeMouse (the facade's icon-pool repell centre);
		//position.disableTimelineX = true — set BEFORE the behaviour reset
		//and preserved by it (it is position config, and position survives
		//deactivateAllBehaviours(true)):
		//sprite.blendMode = 'add' — container-level on this pool's renderer.

		const isSmall = Math.random() < 0.75;

		//deactivateAllBehaviours(true) — everything off except position:
		core.behaviourMask[lane] = BEHAVIOUR_PROGRESS_PARALLAX | BEHAVIOUR_DISABLE_TIMELINE_X;

		//Shape roll — the triangles sync their rotation to the orbit path:
		const ran = Math.random();
		if (ran < 0.06) {
			core.textureId[lane] = FRAME_TRI;
			orbit.flags[lane] |= PATH_FLAG_SYNC_ROTATION;
		} else if (ran < 0.12) {
			core.textureId[lane] = FRAME_TRI2;
			orbit.flags[lane] |= PATH_FLAG_SYNC_ROTATION;
		} else {
			core.textureId[lane] = FRAME_SOLID2;
		}

		//hardSetScale(0.2):
		core.scale[lane] = 0.2;
		core.tScale[lane] = 0.2;
		core.tScaleSaved[lane] = 0.2;

		//mouseParallax stays off (bit not re-added). setEase x/y 12 scale 20
		//(the 12 + rand*12 above it is immediately overwritten — reference):
		core.easeX[lane] = 12;
		core.easeY[lane] = 12;
		core.easeScale[lane] = 20;

		//repell.randomisePower(30, 80) is overwritten by the block below;
		//orbit parked until the trigger re-enables it:
		orbit.expandTarget[lane] = 1;

		//repell: PUSH at radius 50 off homeMouse (power set but unused by
		//PUSH — stage-3 documented dead write, value kept for state parity):
		core.repellType[lane] = REPELL_PUSH;
		core.repellRadius[lane] = 50;
		core.repellPower[lane] = particleRandom(50, 10);
		core.behaviourMask[lane] |= BEHAVIOUR_REPELL;

		//Colour / scale / alpha rolls:
		if (!isSmall) {
			if (Math.random() < 0.15) {
				core.tint[lane] = hexToTint(HomeColours.getRandomColour(HomeColours.greenBlues));
				core.tScale[lane] = particleRandom(0.15, 0.35);
				core.tAlpha[lane] = particleRandom(0.5, 0.3);
			} else {
				core.tint[lane] = hexToTint('#fff');
				core.tScale[lane] = particleRandom(0.1, 0.25);
				core.tAlpha[lane] = particleRandom(0.1, 0.4);
			}
		} else {
			if (Math.random() < 0.2) {
				core.tint[lane] = hexToTint(HomeColours.getRandomColour(HomeColours.greenBlues));
				core.tAlpha[lane] = particleRandom(0.4, 0.2);
			} else {
				core.tint[lane] = hexToTint('#fff');
				core.tAlpha[lane] = particleRandom(0.1, 0.4);
			}

			core.tScale[lane] = particleRandom(0.08, 0.08);
		}

		this._triggerHomeEffectSwitch(lane);
	}

	/** switchHomeEffect(power): the counter arm is dead in the reference
	 *  (no start() -> NaN count -> never fires) and not ported; the live
	 *  effect is repell-on + the trigger switch. */
	private _switchHomeEffect(lane: number): void {
		this._core.behaviourMask[lane] |= BEHAVIOUR_REPELL;
		this._triggerHomeEffectSwitch(lane);
	}

	/** IconParticleProcessor._triggerHomeEffectSwitch, lane-for-line. */
	private _triggerHomeEffectSwitch(lane: number): void {
		const core = this._core;
		const orbit = core.orbitPath;

		const ease = 20 + Math.random() * 30;

		//initEase == ease (the drag branch is disabled in the reference):
		core.easeX[lane] = ease;
		core.easeY[lane] = ease;
		core.easeScale[lane] = 40;

		core.behaviourMask[lane] |= BEHAVIOUR_REPELL;

		//Orbit on: unit circle, speed 1 eased down to a 0.1 multiplier:
		core.behaviourMask[lane] |= BEHAVIOUR_PATH_ORBIT;
		orbit.expandTarget[lane] = 1;
		orbit.lookupId[lane] = LOOKUP_CIRCLE;
		orbit.speed[lane] = 1;
		orbit.speedMulTarget[lane] = 0.1;
		orbit.flags[lane] &= ~PATH_FLAG_SCALE_ACTIVE; //scaleActive = false (alphaActive is a reference no-op)

		//startBreathing(targetScale, rand*0.03, rand*0.3) — base is the
		//target at call time; startChangeValue 0, startDirection 1:
		core.breathBase[lane] = core.tScale[lane];
		core.breathChange[lane] = Math.random() * 0.03;
		core.breathMax[lane] = Math.random() * 0.3;
		core.breathAdd[lane] = 0;
		core.breathDir[lane] = 1;
		core.behaviourMask[lane] |= BEHAVIOUR_BREATHING;

		//DelayHelper.setTimeout(20ms) -> 1.2 frames in the same tick-time
		//domain: the orbit multipliers land a beat later:
		this._deps.scripts.addMs(lane, 20, (l: number) => {
			orbit.xMul[l] = particleRandom(0, 10);
			orbit.yMul[l] = particleRandom(0, 10);
			core.behaviourMask[l] |= BEHAVIOUR_PATH_ORBIT;
		});
	}
}
